import { Result } from '@praha/byethrow';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import type { ITemplateMetadata } from '@/store/templateManagement/types';
import { getAccessToken, getGlobalToken } from '../../../auth/oktaConfig';
import {
  type IDocumentMapping,
  type ITemplateAppliedFromDynamoDB,
  type ITemplatesAppliedFromMongoDB,
} from '../../../store/applyTemplate/types';
import type { ISettings, IUserSettings } from '../../../store/pageFrame/types';
import type { IGroup, IDocument } from '../../../store/senderBuild/types';
import type { ITemplateRecipient, ITemplate, ITemplateTag } from '../../../store/templateCreate/template-types';
import type { ITemplateUser } from '../../../store/templateSharing/types';
import { TEMPLATES_URL } from '../../environment-config';
import { allOfficeGuid } from '../../utils';
import { headers, primeAuthHeaders } from '../common/helper';
import { updateProgressPercentageForDocument } from '../common/progressPercentage';

export * from './blocks/blocks-api';

export async function getPdfBlobFromUrl(downloadUrl: string): Promise<ArrayBuffer> {
  const accessToken = await getAccessToken();
  const { data } = await axios.get(downloadUrl, { responseType: 'arraybuffer', ...headers(accessToken) });
  return data;
}

export interface ITemplateResponse<T> {
  result: T;
  status: number;
}

export async function graphQlRequest(query: string) {
  const accessToken = (await getGlobalToken()) || (await getAccessToken());
  return await axios.post(`${TEMPLATES_URL}/templatesgraphql`, { query }, headers(accessToken));
}

export async function getTemplates(args: { fetchShares: boolean }): Promise<ITemplateResponse<ITemplateMetadata[]>> {
  const { fetchShares = false } = args;

  if (window.location.search.includes('?simulateEmpty')) {
    return {
      result: [],
      status: 200,
    };
  }
  const { data, status } = await graphQlRequest(
    `{
      templates(fetchShares: ${fetchShares}) {
      id
      name
      tags {
        tagTypeId
        value
      }
      metadata {
        numberOfSigners
      },
      createdByName
      createdBy
      createdDate
    }
  }`
  );
  return {
    result: data.data.templates,
    status,
  };
}

export async function getTemplate(templateId: string, fetchShares?: boolean): Promise<ITemplateResponse<ITemplate>> {
  const { data, status } = await graphQlRequest(
    `{
    template(templateId: "${templateId}") {
      id
      name
      documents {
        id
        order
        name
        numPages
        pageDimensions {
          height
          width
        }
        links {
          downloadPDF {
            href
          }
        }
        blockGroups {
          id
          documentId
          pageNumber
          blockIds
          validation {
            rule
            value
            valueB
          }
        }
        blocks {
          blockId: id
          pageNumber
          blockType
          value
          width
          height
          x
          y
          required
          assignedTo
          fontSize
          color
          strikeType
          groupId: blockGroupId
          documentId
        }
      }
      ${
        fetchShares
          ? `shares {
        sharedTo {
          id
          shareType
        }
        userExclusions
      }`
          : ''
      }
      recipients {
        id
        role
        recipientTitle
      }
    }
  }`
  );
  return {
    result: data.data.template,
    status,
  };
}

export async function createTemplate(): Promise<ITemplateResponse<ITemplateMetadata>> {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.post(TEMPLATES_URL + '/templates', {}, headers(accessToken));
  return {
    result: data,
    status,
  };
}

export async function createEnvelopeFromTemplate(args: { templateId: string; impersonatedPrimeUserGuid?: string }) {
  const { templateId, impersonatedPrimeUserGuid } = args;

  const accessToken = (await getGlobalToken()) || (await getAccessToken());
  const claims = jwt_decode(accessToken);
  const body: { primeUserId?: string } = {};
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  if (impersonatedPrimeUserGuid || claims.prime_user_guid) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    body.primeUserId = impersonatedPrimeUserGuid || claims.prime_user_guid;
  }

  const { data: result } = await axios.post<Result.Result<{ envelopeId: string }, string[]>>(
    TEMPLATES_URL + `/templates/${templateId}/envelope`,
    body,
    headers(accessToken)
  );

  // This code should be unreachable because the api will return an error status code if the
  // request fails, which will cause axios to throw an error before reaching this point.
  if (Result.isFailure(result)) throw new Error(result.error.join(', '));

  return result;
}

export async function applyTemplatesInBulk(
  envelopeId: string,
  body: IDocumentMapping[]
): Promise<ITemplateResponse<ITemplatesAppliedFromMongoDB>> {
  const accessToken = (await getGlobalToken()) || (await getAccessToken());
  try {
    const { data, status } = await axios.put(
      `${TEMPLATES_URL}/templateApplications/${envelopeId}`,
      { templateApplications: body },
      headers(accessToken)
    );
    return { result: data, status };
  } catch (error: any) {
    const errorMessage = error.response?.data?.errors?.[0] || error.message || 'Error applying template';
    throw new Error(errorMessage);
  }
}

export async function updateTemplate(
  templateId: string,
  patchBody: Partial<ITemplate>
): Promise<ITemplateResponse<ITemplate>> {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.patch(
    `${TEMPLATES_URL}/templates/${templateId}`,
    patchBody,
    headers(accessToken)
  );
  return { result: data, status };
}

export async function reorderDocumentsInTemplate(
  templateId: string,
  orderedDocumentIds: string[]
): Promise<ITemplateResponse<ITemplate>> {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.patch(
    `${TEMPLATES_URL}/templates/${templateId}`,
    { orderedDocumentIds },
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
}

export async function uploadDocumentToTemplate(
  templateId: string,
  file: File,
  temporaryDocumentId: string
): Promise<ITemplateResponse<IDocument>> {
  const accessToken = await getAccessToken();
  const formData = new FormData();
  formData.append('name', file.name);
  formData.append('pdf', file);
  const { data, status } = await axios.post(`${TEMPLATES_URL}/templates/${templateId}/documents`, formData, {
    onUploadProgress(e) {
      updateProgressPercentageForDocument(e, temporaryDocumentId);
    },
    ...headers(accessToken),
  });
  return {
    result: data,
    status,
  };
}

export async function addRecipientToTemplate(
  templateId: string,
  recipient: ITemplateRecipient
): Promise<ITemplateResponse<ITemplateRecipient>> {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.post(
    `${TEMPLATES_URL}/templates/${templateId}/recipients`,
    recipient,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
}

export async function updateTemplateRecipient(
  templateId: string,
  recipientId: string,
  patchBody: Partial<ITemplateRecipient>
): Promise<ITemplateResponse<ITemplateRecipient>> {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.patch(
    `${TEMPLATES_URL}/templates/${templateId}/recipients/${recipientId}`,
    patchBody,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
}

export async function deleteDocument(templateId: string, documentId: string): Promise<ITemplateResponse<null>> {
  const accessToken = await getAccessToken();
  const { status } = await axios.delete(
    `${TEMPLATES_URL}/templates/${templateId}/documents/${documentId}`,
    headers(accessToken)
  );
  return {
    result: null,
    status,
  };
}

export async function deleteTemplate(templateId: string): Promise<ITemplateResponse<null>> {
  const accessToken = await getAccessToken();
  const { status } = await axios.delete(`${TEMPLATES_URL}/templates/${templateId}`, headers(accessToken));
  return {
    result: null,
    status,
  };
}

export async function getUserAndAccountSettings(): Promise<ITemplateResponse<ISettings>> {
  const accessToken = await getAccessToken();

  // Template service completely ignores `accountId` and `userId` passed in the query.
  // Instead of also updating the UI they just confusing left them there.
  // @see: https://github.com/skyslope/digisign3-template-service/blob/dev/src/graphql/router.ts#L136
  const { data, status } = await axios.post(
    `${TEMPLATES_URL}/templatesgraphql`,
    {
      query: `{
    accountSettings(accountId: "COMPLETELY_IGNORED_BY_TEMPLATE_SERVICE") {
    template {
        recipientTitles
        blockTypes {
          disabled
        }
        tagging {
          enabled
          tagTypes {
            id
            name
            displayType
            placeholderText
            helperText
            options
            iconName
          }
        }
      }
      envelope {
        blockTypes {
          disabled
        }
      }
      userOrigin
    }
    userSettings(userId: "COMPLETELY_IGNORED_BY_TEMPLATE_SERVICE") {
      autoStampDateTime {
        preference
      }
      dateFormat
      keepBlocksSelected
      emailDocsAsAttachment
      dateFormat
      automatedReminders {
        enabled
      }
    }
  }`,
    },
    headers(accessToken)
  );
  return {
    result: {
      account: data.data.accountSettings,
      user: data.data.userSettings,
    },
    status,
  };
}

export async function updateUserSettings(settings: Partial<IUserSettings>): Promise<ITemplateResponse<IUserSettings>> {
  const accessToken = await getAccessToken();
  const claims: any = jwt_decode(accessToken);
  const { data, status } = await axios.patch(
    `${TEMPLATES_URL}/users/${claims.delegated_user_id || claims.uid || claims.user_id || claims.UserId}/settings`,
    settings,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
}

export async function getTemplateTags(templateId: string): Promise<ITemplateResponse<ITemplateTag[]>> {
  const { data, status } = await graphQlRequest(
    `{
    template(templateId: "${templateId}") {
      name
      tags {
        tagTypeId
        value
      }
    }
  }`
  );
  return {
    result: data.data.template,
    status,
  };
}

export async function getTemplatePreview(templateId: string): Promise<ITemplateResponse<Partial<ITemplate>>> {
  const { data, status } = await graphQlRequest(
    `{
    template(templateId: "${templateId}") {
      documents {
        blocks {
          blockId: id
          pageNumber
          blockType
          value
          width
          height
          x
          y
          required
          assignedTo
          fontSize
          color
          strikeType
        }
        numPages
      }
      recipients {
        id
        role
      }
    }
  }`
  );
  return {
    result: data.data.template,
    status,
  };
}

export async function createNewGroup(templateId: string, group: IGroup) {
  const accessToken = await getAccessToken();
  const postBody = {
    blockType: 'Checkbox',
    pageNumber: group.pageNumber,
    validation: group.validation,
    blockIds: group.blockIds,
  };
  const { data, status } = await axios.post(
    `${TEMPLATES_URL}/templates/${templateId}/documents/${group.documentId}/blockGroups`,
    postBody,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
}

export async function editBlockGroup(templateId: string, group: IGroup) {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.put(
    `${TEMPLATES_URL}/templates/${templateId}/documents/${group.documentId}/blockGroups/${group.id}`,
    group,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
}

export async function deleteBlockGroup(templateId: string, documentId: string, groupId: string) {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.delete(
    `${TEMPLATES_URL}/templates/${templateId}/documents/${documentId}/blockGroups/${groupId}`,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
}

export async function deleteRecipient(templateId: string, recipientId: string) {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.delete(
    `${TEMPLATES_URL}/templates/${templateId}/recipients/${recipientId}`,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
}

export async function getTemplatesApplied(
  envelopeId: string
): Promise<ITemplateResponse<ITemplatesAppliedFromMongoDB | ITemplateAppliedFromDynamoDB[]>> {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.get(`${TEMPLATES_URL}/templateApplications/${envelopeId}`, headers(accessToken));
  return {
    result: data,
    status,
  };
}

export async function toggleShareTemplateToUser(
  user: ITemplateUser,
  templateId: string,
  sharedToAllParentGroups: boolean,
  sharedToASubGroup: boolean,
  sharedToAnOffice: boolean
) {
  const accessToken = (await getGlobalToken()) || (await getAccessToken());
  let requestBody: any = {};
  if (sharedToAllParentGroups || sharedToASubGroup || sharedToAnOffice) {
    if (user.isTemplateShared) {
      // the user was previously excluded from the template and now we are sharing to them
      requestBody.remove = {
        userExclusions: [`${user.oktaUserId}`],
      };
    } else {
      // the template was previously shared to user via their parent groups or via an office and now we are excluding them
      requestBody.add = {
        userExclusions: [`${user.oktaUserId}`],
      };
    }
  } else {
    if (user.isTemplateShared) {
      requestBody = {
        add: {
          sharedTo: [
            {
              id: user.oktaUserId,
              shareType: 'User',
            },
          ],
        },
        remove: {
          userExclusions: [`${user.oktaUserId}`],
        },
      };
    } else {
      requestBody.remove = {
        sharedTo: [
          {
            id: user.oktaUserId,
            shareType: 'User',
          },
        ],
      };
    }
  }
  // if the template is shared to all users parent groups, we only manage userExclusions in the request
  // else we just manage the individual user shares
  await axios.put(`${TEMPLATES_URL}/templates/${templateId}/shares`, requestBody, headers(accessToken));
}

export async function toggleShareTemplateToASubGroupOrOffice(
  templateId: string,
  id: string,
  alreadyShared: boolean,
  shareType?: string
) {
  const accessToken = (await getGlobalToken()) || (await getAccessToken());
  let requestBody: any = {};
  if (alreadyShared) {
    requestBody.remove = {
      sharedTo: [
        {
          id,
          shareType,
        },
      ],
    };
  } else {
    requestBody.add = {
      sharedTo: [
        {
          id,
          shareType,
        },
      ],
    };
  }
  await axios.put(`${TEMPLATES_URL}/templates/${templateId}/shares`, requestBody, primeAuthHeaders(accessToken));
}

export async function toggleShareTemplateToAllUsersParentGroups(
  alreadyShared: boolean,
  templateId: string,
  officeGuids = new Array<string>()
) {
  const accessToken = (await getGlobalToken()) || (await getAccessToken());
  let requestBody: any = {};
  if (alreadyShared) {
    requestBody.remove = {
      sharedTo: [
        {
          shareType: 'AllGroups',
        },
        {
          shareType: 'AllUsers',
        },
      ],
    };
  } else {
    requestBody.add = {
      sharedTo: [
        {
          shareType: 'AllGroups',
        },
      ],
    };
    if (officeGuids && officeGuids.length) {
      requestBody.remove = { sharedTo: [] };
      officeGuids.forEach((o) => {
        requestBody.remove.sharedTo.push({
          id: o,
          shareType: o === allOfficeGuid ? 'AllOffice' : 'Office',
        });
      });
    }
  }
  await axios.put(`${TEMPLATES_URL}/templates/${templateId}/shares`, requestBody, headers(accessToken));
}
